import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "../../utils/toast";
import { AlertTriangle, Check, Loader2, Search, UserRound, X } from "lucide-react";
import { fetchTests, fetchTestCategories } from "../../store/slices/testSlice";
import { fetchQuestionsByTest } from "../../store/slices/dynamicQuestionSlice";
import { createVisit } from "../../store/slices/visitSlice";
import { createTestOrder } from "../../store/slices/testOrderSlice";

const TestOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Get patient passed from PatientSearch
  const incoming = location.state?.patient || null;
  const patient = incoming?.id
    ? incoming
    : incoming?.data
    ? incoming.data
    : null;

  const {
    tests,
    testCategories,
    loading: testsLoading,
  } = useSelector((s) => s.tests);
  const { loading: orderLoading } = useSelector((s) => s.testOrders);
  const { questionsByTest } = useSelector((s) => s.dynamicQuestions);
  const { user } = useSelector((s) => s.auth || {});

  const [selectedTests, setSelectedTests] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState("unpaid");
  const [dynamicAnswers, setDynamicAnswers] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loadingQuestionsForTest, setLoadingQuestionsForTest] = useState(null);

  const asMoneyNumber = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const cleaned = value.replace(/[^0-9.-]/g, "");
      const n = Number(cleaned);
      return Number.isFinite(n) ? n : 0;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  useEffect(() => {
    dispatch(fetchTests());
    dispatch(fetchTestCategories());
  }, [dispatch]);

  // If no patient, guide user back
  if (!patient) {
    return (
      <div className="pt-6">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200/70">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-red-50 ring-1 ring-red-200">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900">No patient selected</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Select a patient first, then come back to place orders.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    onClick={() => navigate("/receptionist/search-patient")}
                    className="inline-flex items-center justify-center rounded-xl bg-[#235F72] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                  >
                    Go to Patient Search
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const categoryChips = [
    { id: "all", name: "All Tests" },
    ...((testCategories || []).map((c) => ({ id: c.id, name: c.name })) || []),
  ];

  const filteredTests = tests.filter((test) => {
    const matchesSearch =
      test.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      test.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || test.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getTestQuestions = (testId) => questionsByTest[testId] || [];

  const handleTestToggle = async (test) => {
    if (selectedTests.find((t) => t.id === test.id)) {
      setSelectedTests((prev) => prev.filter((t) => t.id !== test.id));
      setDynamicAnswers((prev) => {
        const next = { ...prev };
        delete next[test.id];
        return next;
      });
    } else {
      setSelectedTests((prev) => [...prev, test]);
      if (!questionsByTest[test.id]) {
        setLoadingQuestionsForTest(test.id);
        try {
          await dispatch(fetchQuestionsByTest(test.id)).unwrap();
        } catch (e) {
          console.error("Failed to fetch questions for test:", test.id, e);
          toast.error(`Failed to load questions for ${test.name}`);
        } finally {
          setLoadingQuestionsForTest(null);
        }
      }
    }
  };

  const handleDynamicAnswer = (testId, questionId, value) => {
    setDynamicAnswers((prev) => ({
      ...prev,
      [testId]: { ...(prev[testId] || {}), [questionId]: value },
    }));
  };

  const renderDynamicQuestion = (testId, question) => {
    const currentAnswer = dynamicAnswers[testId]?.[question.id];
    switch (question.field_type) {
      case "radio":
        return (
          <div key={question.id} className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {question.question_text} {question.is_required && "*"}
            </label>
            <div className="flex flex-wrap gap-2">
              {question.options?.map((option) => (
                <label key={option} className="flex items-center text-xs">
                  <input
                    type="radio"
                    name={`${testId}-${question.id}`}
                    value={option}
                    checked={currentAnswer === option}
                    onChange={(e) =>
                      handleDynamicAnswer(testId, question.id, e.target.value)
                    }
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="mr-1 text-[#36F1A2] focus:ring-[#36F1A2]"
                    required={question.is_required}
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>
        );
      case "checkbox":
        return (
          <div key={question.id} className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {question.question_text} {question.is_required && "*"}
            </label>
            <div className="flex flex-wrap gap-2">
              {question.options?.map((option) => (
                <label key={option} className="flex items-center text-xs">
                  <input
                    type="checkbox"
                    checked={currentAnswer?.includes?.(option) || false}
                    onChange={(e) => {
                      const curr = currentAnswer || [];
                      const next = e.target.checked
                        ? [...curr, option]
                        : curr.filter((v) => v !== option);
                      handleDynamicAnswer(testId, question.id, next);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="mr-1 text-[#36F1A2] focus:ring-[#36F1A2]"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>
        );
      case "text":
        return (
          <div key={question.id} className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {question.question_text} {question.is_required && "*"}
            </label>
            <input
              type="text"
              value={currentAnswer || ""}
              onChange={(e) =>
                handleDynamicAnswer(testId, question.id, e.target.value)
              }
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              placeholder={question.placeholder}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#36F1A2] focus:border-transparent"
            />
          </div>
        );
      case "dropdown":
        return (
          <div key={question.id} className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {question.question_text} {question.is_required && "*"}
            </label>
            <select
              value={currentAnswer || ""}
              onChange={(e) =>
                handleDynamicAnswer(testId, question.id, e.target.value)
              }
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#36F1A2] focus:border-transparent"
              required={question.is_required}
            >
              <option value="">Select an option</option>
              {question.options?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        );
      default:
        return null;
    }
  };

  const isTestReadyForSubmission = (test) => {
    const testQuestions = getTestQuestions(test.id);
    if (!testQuestions || testQuestions.length === 0) return true;
    const answers = dynamicAnswers[test.id] || {};
    return testQuestions.every((q) => {
      if (!q.is_required) return true;
      const a = answers[q.id];
      if (q.field_type === "checkbox") return Array.isArray(a) && a.length > 0;
      return a && a.toString().trim() !== "";
    });
  };

  const areAllTestsReady = () =>
    selectedTests.every((test) => isTestReadyForSubmission(test));

  const calculateTotal = () =>
    selectedTests.reduce((sum, t) => sum + asMoneyNumber(t.price), 0);

  const handleSubmitOrder = async () => {
    if (selectedTests.length === 0) {
      toast.error("Please select at least one test");
      return;
    }
    if (!areAllTestsReady()) {
      toast.error("Please answer all required questions for selected tests");
      return;
    }

    try {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      const localDate = `${yyyy}-${mm}-${dd}`;
      const visitPayload = {
        patient_id: patient.id,
        receptionist_id: user?.id || null,
        visit_date: localDate,
        visit_time: now.toTimeString().slice(0, 5),
        status: "registered",
        priority: "routine",
      };
      const visit = await dispatch(createVisit(visitPayload)).unwrap();
      const visitId = visit?.id ?? visit?.data?.id;

      const orderPromises = selectedTests.map((test) =>
        dispatch(
          createTestOrder({
            visit_id: visitId,
            test_id: test.id,
            priority: "normal",
            payment_status: paymentStatus,
            sample_type: test.sample_type || "blood",
            dynamic_answers: dynamicAnswers[test.id] || {},
          })
        ).unwrap()
      );
      await Promise.all(orderPromises);

      toast.success("Visit and test orders created successfully!");
      navigate("/receptionist/dashboard");
    } catch (error) {
      console.error("Failed to create visit/test orders:", error);
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to create visit or test orders"
      );
    }
  };

  const isLoading = orderLoading || testsLoading;
  const total = calculateTotal();

  return (
    <div className="pt-6">
      <div className="mx-auto max-w-7xl">
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
          <div className="border-t-4 border-[#36F1A2] bg-gradient-to-r from-gray-50 to-white px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#36F1A2]/15 px-3 py-1 text-xs font-semibold text-[#235F72] ring-1 ring-[#36F1A2]/40">
                  <UserRound className="h-4 w-4" />
                  Step 2 of 2
                </div>
                <h1 className="mt-2 text-xl font-semibold text-gray-900">Order laboratory tests</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Select tests, answer any required questions, then create the orders.
                </p>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-2xl bg-white/70 px-4 py-3 ring-1 ring-gray-200 backdrop-blur">
                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Patient</div>
                  <div className="mt-1 truncate text-sm font-semibold text-gray-900">
                    {patient.full_name}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-600">Card: {patient.card_number}</div>
                </div>
                <div className="hidden sm:block text-right">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Total</div>
                  <div className="mt-1 text-base font-bold text-[#235F72]">ETB {total}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-5 py-5 sm:px-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
              <div className="min-w-0">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <h2 className="text-sm font-semibold text-gray-900">Select tests</h2>
                    <p className="mt-0.5 text-xs text-gray-600">Search and filter by category.</p>
                  </div>
                </div>

                <div className="mt-4 space-y-4">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search tests…"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full rounded-xl bg-white py-2.5 pl-9 pr-9 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                    />
                    {searchTerm ? (
                      <button
                        type="button"
                        onClick={() => setSearchTerm("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                        aria-label="Clear search"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>

                  <div className="-mx-1 overflow-x-auto px-1">
                    <div className="flex w-max items-center gap-2">
                      {categoryChips.map((category) => {
                        const active =
                          selectedCategory === category.id ||
                          (category.id !== "all" && selectedCategory === Number(category.id));
                        return (
                          <button
                            key={category.id}
                            type="button"
                            onClick={() =>
                              setSelectedCategory(category.id === "all" ? "all" : Number(category.id))
                            }
                            className={
                              active
                                ? "rounded-full bg-[#235F72] px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-[#235F72]/20"
                                : "rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-200"
                            }
                          >
                            {category.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-gray-50 p-3 ring-1 ring-gray-200/70">
                    <div className="max-h-[28rem] space-y-3 overflow-y-auto pr-1">
                      {testsLoading ? (
                        <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-600">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading tests…
                        </div>
                      ) : filteredTests.length === 0 ? (
                        <div className="py-10 text-center text-sm text-gray-600">
                          No tests match your search/filter.
                        </div>
                      ) : (
                        filteredTests.map((test) => {
                          const isSelected = selectedTests.some((t) => t.id === test.id);
                          const testQuestions = getTestQuestions(test.id);
                          const hasDynamicQuestions = testQuestions.length > 0;
                          const isReady = isTestReadyForSubmission(test);
                          const isLoadingQuestions = loadingQuestionsForTest === test.id;

                          return (
                            <div
                              key={test.id}
                              onClick={() => handleTestToggle(test)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") handleTestToggle(test);
                              }}
                              className={
                                isSelected
                                  ? "cursor-pointer rounded-2xl bg-white p-4 shadow-sm ring-2 ring-[#36F1A2]/60"
                                  : "cursor-pointer rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200 transition hover:ring-gray-300"
                              }
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0">
                                  <div className="flex items-start gap-3">
                                    <div
                                      className={
                                        isSelected
                                          ? "mt-0.5 flex h-5 w-5 items-center justify-center rounded-md bg-[#36F1A2] ring-1 ring-[#36F1A2]"
                                          : "mt-0.5 flex h-5 w-5 items-center justify-center rounded-md bg-white ring-1 ring-gray-300"
                                      }
                                      aria-hidden="true"
                                    >
                                      {isSelected ? <Check className="h-3.5 w-3.5 text-white" /> : null}
                                    </div>

                                    <div className="min-w-0">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <div className="truncate text-sm font-semibold text-gray-900">{test.name}</div>
                                        {hasDynamicQuestions ? (
                                          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-[#085DB6] ring-1 ring-blue-200">
                                            Questions
                                          </span>
                                        ) : null}
                                        {isSelected && hasDynamicQuestions && !isReady ? (
                                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800 ring-1 ring-amber-200">
                                            Required
                                          </span>
                                        ) : null}
                                        {isLoadingQuestions ? (
                                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700 ring-1 ring-gray-200">
                                            Loading…
                                          </span>
                                        ) : null}
                                      </div>
                                      {test.description ? (
                                        <div className="mt-1 line-clamp-2 text-xs text-gray-600">{test.description}</div>
                                      ) : null}
                                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                                        {test.sample_type ? <span>Sample: {test.sample_type}</span> : null}
                                        {test.processing_time ? <span>Time: {test.processing_time}</span> : null}
                                        {test.tube_type ? <span>Tube: {test.tube_type}</span> : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="shrink-0 text-right">
                                  <div className="text-lg font-bold text-[#235F72]">ETB {asMoneyNumber(test.price)}</div>
                                  {test.category_name ? (
                                    <div className="mt-1 text-xs text-gray-600">{test.category_name}</div>
                                  ) : null}
                                </div>
                              </div>

                              {isSelected && hasDynamicQuestions ? (
                                <div
                                  className="mt-4 rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200"
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="text-xs font-semibold text-gray-900">
                                      Additional information
                                    </div>
                                    {!isReady ? (
                                      <div className="text-xs font-semibold text-red-600">Required fields missing</div>
                                    ) : null}
                                  </div>
                                  <div className="mt-3 space-y-3">
                                    {testQuestions.map((q) => (
                                      <div key={q.id}>{renderDynamicQuestion(test.id, q)}</div>
                                    ))}
                                  </div>
                                </div>
                              ) : null}

                              {isSelected && isLoadingQuestions ? (
                                <div className="mt-4 rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200">
                                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Loading questions…
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <button
                    type="button"
                    onClick={() => navigate("/receptionist/search-patient")}
                    className="inline-flex items-center justify-center rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                  >
                    Back to Patient Search
                  </button>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmitOrder}
                      disabled={isLoading || selectedTests.length === 0 || !areAllTestsReady()}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#235F72] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Creating…
                        </>
                      ) : (
                        "Create Test Orders"
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <aside className="min-w-0">
                <div className="sticky top-6 rounded-2xl bg-white p-4 ring-1 ring-gray-200/70 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Order summary</div>
                      <div className="mt-1 text-xs text-gray-600">
                        {selectedTests.length} test(s) selected
                      </div>
                    </div>
                    {!areAllTestsReady() && selectedTests.length > 0 ? (
                      <div className="text-xs font-semibold text-red-600">Complete required questions</div>
                    ) : null}
                  </div>

                  {selectedTests.length === 0 ? (
                    <div className="mt-4 rounded-xl bg-gray-50 p-4 text-sm text-gray-600 ring-1 ring-gray-200">
                      Select one or more tests to see totals here.
                    </div>
                  ) : (
                    <div className="mt-4 space-y-2">
                      <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                        {selectedTests.map((test) => {
                          const hasQuestions = getTestQuestions(test.id).length > 0;
                          const ready = isTestReadyForSubmission(test);
                          return (
                            <div
                              key={test.id}
                              className="flex items-start justify-between gap-3 rounded-xl bg-gray-50 p-3 ring-1 ring-gray-200"
                            >
                              <div className="min-w-0">
                                <div className="truncate text-sm font-semibold text-gray-900">{test.name}</div>
                                {hasQuestions ? (
                                  <div className={ready ? "mt-1 text-xs font-semibold text-emerald-700" : "mt-1 text-xs font-semibold text-amber-800"}>
                                    {ready ? "Ready" : "Incomplete"}
                                  </div>
                                ) : (
                                  <div className="mt-1 text-xs text-gray-600">No questions</div>
                                )}
                              </div>
                              <div className="shrink-0 text-right">
                                <div className="text-sm font-semibold text-[#235F72]">ETB {asMoneyNumber(test.price)}</div>
                                <button
                                  type="button"
                                  onClick={() => handleTestToggle(test)}
                                  className="mt-1 text-xs font-semibold text-red-600 hover:text-red-700"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="mt-3 flex items-center justify-between rounded-xl bg-white px-3 py-3 ring-1 ring-gray-200">
                        <div className="text-sm font-semibold text-gray-900">Total</div>
                        <div className="text-xl font-bold text-[#235F72]">ETB {total}</div>
                      </div>

                      <div className="mt-3 rounded-xl bg-white p-3 ring-1 ring-gray-200">
                        <label className="mb-1 block text-xs font-semibold text-gray-700">
                          Payment status
                        </label>
                        <select
                          value={paymentStatus}
                          onChange={(e) => setPaymentStatus(e.target.value)}
                          className="w-full rounded-xl bg-white px-3 py-2 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                        >
                          <option value="unpaid">Unpaid</option>
                          <option value="partial">Partial</option>
                          <option value="paid">Paid</option>
                          <option value="refunded">Refunded</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestOrders;
